import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { TelegramUser } from '@/types/telegram'

// 統一的 Telegram 用戶資料介面
export interface UnifiedTelegramUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
  language_code?: string
  is_premium?: boolean
  is_bot?: boolean
  auth_date?: number
  hash?: string
  
  // 來源標識
  source: 'miniapp' | 'widget'
  
  // 原始數據（用於調試）
  raw_data?: any
}

interface TelegramState {
  // 統一的用戶資料
  telegramUser: UnifiedTelegramUser | null
  
  // Mini App 專用狀態
  webApp: any | null
  initData: string
  platform: string
  colorScheme: 'light' | 'dark'
  
  // 狀態管理
  isReady: boolean
  source: 'miniapp' | 'widget' | null
  
  // Actions
  setMiniAppUser: (webApp: any, user: TelegramUser) => void
  setWidgetUser: (user: any) => void
  clearUser: () => void
  updateUserData: (data: Partial<UnifiedTelegramUser>) => void
}

export const useTelegramStore = create<TelegramState>()(
  persist(
    (set, get) => ({
      // Initial state
      telegramUser: null,
      webApp: null,
      initData: '',
      platform: 'unknown',
      colorScheme: 'light',
      isReady: false,
      source: null,

      // Set Mini App user data
      setMiniAppUser: (webApp: any, user: TelegramUser) => {
        console.log('Telegram Store: Setting Mini App user:', user)
        
        const unifiedUser: UnifiedTelegramUser = {
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          username: user.username,
          photo_url: user.photo_url,
          language_code: user.language_code,
          is_premium: user.is_premium,
          is_bot: user.is_bot,
          source: 'miniapp',
          raw_data: user
        }

        set({
          telegramUser: unifiedUser,
          webApp,
          initData: webApp?.initData || '',
          platform: webApp?.platform || 'unknown',
          colorScheme: webApp?.colorScheme || 'light',
          isReady: true,
          source: 'miniapp'
        })
      },

      // Set Widget user data
      setWidgetUser: (user: any) => {
        console.log('Telegram Store: Setting Widget user:', user)
        
        const unifiedUser: UnifiedTelegramUser = {
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          username: user.username,
          photo_url: user.photo_url,
          auth_date: user.auth_date,
          hash: user.hash,
          source: 'widget',
          raw_data: user
        }

        set({
          telegramUser: unifiedUser,
          webApp: null,
          initData: '',
          platform: 'web',
          colorScheme: 'light',
          isReady: true,
          source: 'widget'
        })
      },

      // Clear user data
      clearUser: () => {
        console.log('Telegram Store: Clearing user data')
        set({
          telegramUser: null,
          webApp: null,
          initData: '',
          platform: 'unknown',
          colorScheme: 'light',
          isReady: false,
          source: null
        })
      },

      // Update user data
      updateUserData: (data: Partial<UnifiedTelegramUser>) => {
        const currentUser = get().telegramUser
        if (currentUser) {
          set({
            telegramUser: { ...currentUser, ...data }
          })
        }
      }
    }),
    {
      name: 'telegram-storage',
      partialize: (state) => ({
        telegramUser: state.telegramUser,
        platform: state.platform,
        colorScheme: state.colorScheme,
        source: state.source
      }),
    }
  )
)